import React, { useState } from 'react';
import './Print.css';

function Print() {
  const [connectedDevice, setConnectedDevice] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [status, setStatus] = useState('Haz clic en "Buscar Impresora" para comenzar');
  const [customMessage, setCustomMessage] = useState('¡Hola desde mi aplicación!');
  const [scannedDevice, setScannedDevice] = useState(null);

  const PRINTER_SERVICE_UUID = [
    '000018f0-0000-1000-8000-00805f9b34fb', // Servicio estándar de impresora
    '0000ff00-0000-1000-8000-00805f9b34fb',   // Servicio genérico
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',   // Servicio específico del fabricante
    '0000ae00-0000-1000-8000-00805f9b34fb',   // Otro servicio común
    '49535343-fe7d-4ae5-8fa9-9fafd205e455',   // Servicio de algunas impresoras chinas
    '00001101-0000-1000-8000-00805f9b34fb',   // Serial Port Profile (SPP)
    '0000a000-0000-1000-8000-00805f9b34fb'    // Otro servicio común
  ];

  const PRINT_CHARACTERISTIC_UUID = [
    '00002af1-0000-1000-8000-00805f9b34fb', // Característica estándar de impresión
    '0000ff02-0000-1000-8000-00805f9b34fb',   // Característica genérica
    'bef8d6c9-9c21-4c9e-b632-bd58c1009f9f',   // Característica específica del fabricante
    'e7810a71-73ae-499d-8c15-faa9aef0c3f2',   // Otra característica
    '0000ae01-0000-1000-8000-00805f9b34fb',   // Otra característica
    '00002af0-0000-1000-8000-00805f9b34fb',   // Característica de informe
    '00002af3-0000-1000-8000-00805f9b34fb'    // Característica de estado
  ];

  const scanForDevices = async () => {
    if (!navigator.bluetooth) {
      setStatus('❌ Web Bluetooth no está disponible');
      return;
    }

    setIsConnecting(true);
    setStatus('Escaneando dispositivos Bluetooth...');

    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUID
      });

      setStatus(`Dispositivo encontrado: ${device.name}`);
      console.log('Dispositivo encontrado:', device);
      
      // Guardamos el dispositivo escaneado para poder conectarlo después
      setScannedDevice(device);
      
      setTimeout(() => {
        setStatus('Dispositivo encontrado. Haz clic en "Conectar Dispositivo Escaneado" para conectar');
      }, 3000);

    } catch (error) {
      console.error('Error al escanear:', error);
      if (error.name === 'NotFoundError') {
        setStatus('❌ No se encontraron dispositivos Bluetooth');
      } else {
        setStatus(`❌ Error: ${error.message}`);
      }
    } finally {
      setIsConnecting(false);
    }
  };

  // Función para conectar el dispositivo escaneado
  const connectScannedDevice = async () => {
    if (!scannedDevice) {
      setStatus('❌ No hay dispositivo escaneado para conectar');
      return;
    }

    setIsConnecting(true);
    setStatus(`Conectando a ${scannedDevice.name}...`);

    try {
      console.log('Conectando al dispositivo escaneado:', scannedDevice);

      if (!scannedDevice.gatt) {
        throw new Error('El dispositivo no soporta GATT');
      }

      const server = await scannedDevice.gatt.connect();
      console.log('Conectado al servidor GATT');

      scannedDevice.addEventListener('gattserverdisconnected', onDisconnected);

      setStatus(`✅ Conectado a ${scannedDevice.name}! Listo para imprimir`);

      setConnectedDevice({
        device: scannedDevice,
        server: server,
        name: scannedDevice.name
      });

    } catch (error) {
      console.error('Error completo:', error);
      handleConnectionError(error);
    } finally {
      setIsConnecting(false);
    }
  };
  
  // Comandos ESC/POS para impresoras térmicas
  const ESC = '\x1B';
  const COMMANDS = {
    INIT: ESC + '@',
    ALIGN_LEFT: ESC + '\x61\x00',
    ALIGN_CENTER: ESC + '\x61\x01',
    ALIGN_RIGHT: ESC + '\x61\x02',
    BOLD_ON: ESC + '\x45\x01',
    BOLD_OFF: ESC + '\x45\x00',
    DOUBLE_HEIGHT: ESC + '\x21\x30',
    DOUBLE_WIDTH: ESC + '\x21\x20',
    NORMAL_TEXT: ESC + '\x21\x00',
    CUT: ESC + '\x69',
    FEED_PAPER: '\n\n\n\n\n\n' // 6 líneas de alimentación
  };

  // Función para asegurar que los datos sean Uint8Array
  const ensureUint8Array = (data) => {
    if (typeof data === 'string') {
      return new TextEncoder().encode(data);
    }
    if (data instanceof Uint8Array) {
      return data;
    }
    if (data instanceof ArrayBuffer) {
      return new Uint8Array(data);
    }
    if (ArrayBuffer.isView(data)) {
      return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    }
    throw new Error('Tipo de dato no soportado');
  };

  const connectToPrinter = async () => {
    if (!navigator.bluetooth) {
      alert('Web Bluetooth no está disponible en este navegador. Usa Chrome o Edge.');
      return;
    }

    setIsConnecting(true);
    setStatus('Buscando dispositivos Bluetooth...');

    try {
      console.log('Solicitando dispositivo Bluetooth...');

      // Modificado: Usamos acceptAllDevices para que muestre todos los dispositivos
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: PRINTER_SERVICE_UUID
      });

      console.log('Dispositivo seleccionado:', device);
      setStatus(`Seleccionado: ${device.name}. Conectando...`);

      if (!device.gatt) {
        throw new Error('El dispositivo no soporta GATT');
      }

      const server = await device.gatt.connect();
      console.log('Conectado al servidor GATT');

      device.addEventListener('gattserverdisconnected', onDisconnected);

      setStatus(`✅ Conectado a ${device.name}! Listo para imprimir`);

      setConnectedDevice({
        device: device,
        server: server,
        name: device.name
      });

    } catch (error) {
      console.error('Error completo:', error);
      handleConnectionError(error);
    } finally {
      setIsConnecting(false);
    }
  };

  const onDisconnected = () => {
    setStatus('❌ Dispositivo desconectado');
    setConnectedDevice(null);
    setScannedDevice(null);
  };

  const handleConnectionError = (error) => {
    if (error.name === 'NotFoundError') {
      setStatus('❌ No se seleccionó ningún dispositivo');
    } else if (error.name === 'NotAllowedError') {
      setStatus('❌ Permiso denegado para Bluetooth');
    } else if (error.name === 'NetworkError') {
      setStatus('❌ Error de conexión - verifica que la impresora esté encendida');
    } else {
      setStatus(`❌ Error: ${error.message}`);
    }
  };

  const findPrinterService = async (server) => {
    setStatus('Buscando servicio de impresión...');

    for (const serviceUUID of PRINTER_SERVICE_UUID) {
      try {
        const service = await server.getPrimaryService(serviceUUID);
        console.log(`✅ Servicio de impresión encontrado: ${serviceUUID}`);
        setStatus(`Servicio de impresión encontrado: ${serviceUUID}`);
        return service;
      } catch (error) {
        console.log(`❌ Servicio ${serviceUUID} no disponible`);
      }
    }

    setStatus('Buscando en todos los servicios disponibles...');
    const services = await server.getPrimaryServices();
    console.log('Todos los servicios disponibles:', services);

    if (services.length === 0) {
      throw new Error('No se encontraron servicios en el dispositivo');
    }

    for (const service of services) {
      try {
        const characteristics = await service.getCharacteristics();
        console.log(`Características en servicio ${service.uuid}:`, characteristics);

        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            console.log(`✅ Característica de escritura encontrada en servicio: ${service.uuid}`);
            setStatus(`Servicio compatible encontrado: ${service.uuid}`);
            return service;
          }
        }
      } catch (error) {
        console.log(`Error accediendo al servicio ${service.uuid}:`, error);
      }
    }

    throw new Error('No se encontró ningún servicio con características de escritura');
  };

  const findWriteCharacteristic = async (service) => {
    console.log('Buscando característica de escritura en servicio:', service.uuid);

    for (const charUUID of PRINT_CHARACTERISTIC_UUID) {
      try {
        const characteristic = await service.getCharacteristic(charUUID);
        if (characteristic.properties.write || characteristic.properties.writeWithoutResponse) {
          console.log(`✅ Característica de escritura específica encontrada: ${charUUID}`);
          return characteristic;
        }
      } catch (error) {
        console.log(`Característica específica ${charUUID} no encontrada`);
      }
    }

    const characteristics = await service.getCharacteristics();
    console.log('Todas las características disponibles:', characteristics);

    for (const char of characteristics) {
      if (char.properties.write || char.properties.writeWithoutResponse) {
        console.log(`✅ Característica de escritura seleccionada: ${char.uuid}`);
        return char;
      }
    }

    throw new Error('No se encontró característica de escritura en este servicio');
  };

  // Función para enviar datos con diferentes métodos
  const sendDataWithMethod = async (characteristic, data, method = 'write') => {
    console.log(`Enviando datos con método: ${method}`);

    try {
      // Asegurarnos de que los datos sean Uint8Array
      const dataArray = ensureUint8Array(data);
      console.log('Datos convertidos a Uint8Array:', dataArray);

      if (method === 'writeWithoutResponse') {
        await characteristic.writeValueWithoutResponse(dataArray);
      } else {
        await characteristic.writeValue(dataArray);
      }
      console.log(`Datos enviados exitosamente con ${method}`);
      return true;
    } catch (error) {
      console.error(`Error con método ${method}:`, error);
      return false;
    }
  };

  const sendPrintData = async (text) => {
    if (!connectedDevice) throw new Error('No hay dispositivo conectado');

    const { server } = connectedDevice;

    const service = await findPrinterService(server);
    const characteristic = await findWriteCharacteristic(service);

    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    console.log(`Enviando ${data.length} bytes`);
    setStatus(`Enviando ${data.length} bytes...`);

    // Probar diferentes métodos de envío
    const methods = ['write', 'writeWithoutResponse'];

    for (const method of methods) {
      console.log(`Intentando con método: ${method}`);
      setStatus(`Intentando con método: ${method}...`);

      const success = await sendDataWithMethod(characteristic, data, method);
      if (success) {
        setStatus(`✅ Datos enviados con método: ${method}`);
        return;
      }
    }

    throw new Error('No se pudo enviar datos con ningún método');
  };

  // Función para imprimir un mensaje personalizado
  const printCustomMessage = async () => {
    if (!connectedDevice || !customMessage.trim()) return;

    setIsPrinting(true);
    setStatus(`Imprimiendo mensaje: "${customMessage}"...`);

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Preparamos el mensaje con comandos ESC/POS
      const encoder = new TextEncoder();
      
      // Comandos para inicializar y formatear el texto
      const initCommand = new Uint8Array([0x1B, 0x40]); // ESC @ - Inicializar impresora
      const alignCenter = new Uint8Array([0x1B, 0x61, 0x01]); // ESC a 1 - Alinear al centro
      const boldOn = new Uint8Array([0x1B, 0x45, 0x01]); // ESC E 1 - Negrita activada
      const boldOff = new Uint8Array([0x1B, 0x45, 0x00]); // ESC E 0 - Negrita desactivada
      const feedPaper = new Uint8Array([0x0A, 0x0A, 0x0A]); // 3 saltos de línea
      
      // Convertir el mensaje a bytes
      const messageBytes = encoder.encode(customMessage + '\n');
      
      // Combinar todos los comandos y el mensaje
      const commands = [
        initCommand,
        alignCenter,
        boldOn,
        messageBytes,
        boldOff,
        feedPaper
      ];

      // Enviar cada comando por separado
      for (let i = 0; i < commands.length; i++) {
        const cmd = commands[i];
        console.log(`Enviando comando ${i + 1}:`, cmd);
        setStatus(`Enviando parte ${i + 1} de ${commands.length}...`);
        
        // Probar ambos métodos de envío
        let success = await sendDataWithMethod(characteristic, cmd, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, cmd, 'writeWithoutResponse');
        }
        
        if (!success) {
          throw new Error(`No se pudo enviar comando ${i + 1}`);
        }
        
        // Pequeña pausa entre comandos
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      setStatus(`✅ Mensaje "${customMessage}" impreso correctamente`);

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para imprimir bytes RAW con diferentes métodos
  const printRawBytesAdvanced = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Enviando bytes RAW (avanzado)...');

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Comandos básicos en diferentes formatos
      const commands = [
        // Comando de inicialización ESC @
        new Uint8Array([0x1B, 0x40]),
        // Salto de línea
        new Uint8Array([0x0A]),
        // Texto simple
        new TextEncoder().encode("TEST\n"),
        // Comando de alimentación de papel
        new Uint8Array([0x1B, 0x64, 0x03]), // ESC d 3 (alimentar 3 líneas)
        // Comando de corte
        new Uint8Array([0x1B, 0x69])
      ];

      for (let i = 0; i < commands.length; i++) {
        const cmd = commands[i];
        console.log(`Enviando comando ${i + 1}:`, cmd);
        setStatus(`Enviando comando ${i + 1}...`);

        // Probar ambos métodos
        let success = await sendDataWithMethod(characteristic, cmd, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, cmd, 'writeWithoutResponse');
        }

        if (!success) {
          throw new Error(`No se pudo enviar comando ${i + 1}`);
        }

        // Pequeña pausa entre comandos
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setStatus('✅ Comandos RAW enviados exitosamente');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para imprimir solo un salto de línea
  const printSingleLineFeed = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Enviando un salto de línea...');

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Solo un salto de línea en diferentes formatos
      const lineFeeds = [
        new Uint8Array([0x0A]), // Salto de línea
        new TextEncoder().encode("\n"), // Salto de línea como texto
        new Uint8Array([0x0D]), // Retorno de carro
        new TextEncoder().encode("\r"), // Retorno de carro como texto
        new Uint8Array([0x0D, 0x0A]), // CRLF
        new TextEncoder().encode("\r\n") // CRLF como texto
      ];

      for (let i = 0; i < lineFeeds.length; i++) {
        const lf = lineFeeds[i];
        console.log(`Enviando salto de línea tipo ${i + 1}:`, lf);
        setStatus(`Enviando salto de línea tipo ${i + 1}...`);

        let success = await sendDataWithMethod(characteristic, lf, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, lf, 'writeWithoutResponse');
        }

        if (success) {
          setStatus(`✅ Salto de línea tipo ${i + 1} enviado!`);
          return;
        }
      }

      throw new Error('No se pudo enviar ningún tipo de salto de línea');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para imprimir comando de inicialización
  const printInitCommand = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Enviando comando de inicialización...');

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Comando de inicialización en diferentes formatos
      const initCommands = [
        new Uint8Array([0x1B, 0x40]), // ESC @
        new TextEncoder().encode("\x1B@"), // ESC @ como texto
        new Uint8Array([0x1B, 0x40, 0x1B, 0x64, 0x03]), // ESC @ + ESC d 3
        new TextEncoder().encode("\x1B@\x1B\x64\x03") // ESC @ + ESC d 3 como texto
      ];

      for (let i = 0; i < initCommands.length; i++) {
        const cmd = initCommands[i];
        console.log(`Enviando comando de inicialización tipo ${i + 1}:`, cmd);
        setStatus(`Enviando comando de inicialización tipo ${i + 1}...`);

        let success = await sendDataWithMethod(characteristic, cmd, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, cmd, 'writeWithoutResponse');
        }

        if (success) {
          setStatus(`✅ Comando de inicialización tipo ${i + 1} enviado!`);
          return;
        }
      }

      throw new Error('No se pudo enviar ningún comando de inicialización');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para imprimir texto plano
  const printPlainText = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Enviando texto plano...');

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Texto simple en diferentes formatos (todos como Uint8Array)
      const texts = [
        new TextEncoder().encode("TEST\n"),
        new TextEncoder().encode("PRUEBA\n"),
        new TextEncoder().encode("HOLA\n"),
        new TextEncoder().encode("TEST\n"),
        new TextEncoder().encode("PRUEBA\n"),
        new TextEncoder().encode("HOLA\n")
      ];

      for (let i = 0; i < texts.length; i++) {
        const text = texts[i];
        console.log(`Enviando texto tipo ${i + 1}:`, text);
        setStatus(`Enviando texto tipo ${i + 1}...`);

        let success = await sendDataWithMethod(characteristic, text, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, text, 'writeWithoutResponse');
        }

        if (success) {
          setStatus(`✅ Texto tipo ${i + 1} enviado!`);
          return;
        }
      }

      throw new Error('No se pudo enviar ningún texto');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para probar todas las características
  const testAllCharacteristics = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Probando todas las características...');

    try {
      const { server } = connectedDevice;
      const services = await server.getPrimaryServices();

      for (const service of services) {
        console.log(`Probando servicio: ${service.uuid}`);
        const characteristics = await service.getCharacteristics();

        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            console.log(`Probando característica: ${char.uuid}`);
            setStatus(`Probando característica: ${char.uuid}...`);

            // Probar con un comando simple
            const testCmd = new Uint8Array([0x0A]); // Salto de línea

            try {
              if (char.properties.write) {
                await char.writeValue(testCmd);
                console.log(`✅ Escritura exitosa en ${char.uuid}`);
                setStatus(`✅ Escritura exitosa en ${char.uuid}`);
                return;
              }

              if (char.properties.writeWithoutResponse) {
                await char.writeValueWithoutResponse(testCmd);
                console.log(`✅ Escritura sin respuesta exitosa en ${char.uuid}`);
                setStatus(`✅ Escritura sin respuesta exitosa en ${char.uuid}`);
                return;
              }
            } catch (error) {
              console.error(`Error en ${char.uuid}:`, error);
            }
          }
        }
      }

      throw new Error('No se pudo escribir en ninguna característica');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función para imprimir con diferentes codificaciones
  const printWithEncodings = async () => {
    if (!connectedDevice) return;

    setIsPrinting(true);
    setStatus('Probando diferentes codificaciones...');

    try {
      const { server } = connectedDevice;
      const service = await findPrinterService(server);
      const characteristic = await findWriteCharacteristic(service);

      // Texto de prueba
      const testText = "TEST\n";

      // Diferentes codificaciones (todas como Uint8Array)
      const encodings = [
        new TextEncoder().encode(testText), // UTF-8
        new Uint8Array([...testText].map(c => c.charCodeAt(0))), // ASCII
        new Uint8Array([0x54, 0x45, 0x53, 0x54, 0x0A]) // Bytes manuales
      ];

      for (let i = 0; i < encodings.length; i++) {
        const encoded = encodings[i];
        console.log(`Probando codificación ${i + 1}:`, encoded);
        setStatus(`Probando codificación ${i + 1}...`);

        let success = await sendDataWithMethod(characteristic, encoded, 'write');
        if (!success) {
          success = await sendDataWithMethod(characteristic, encoded, 'writeWithoutResponse');
        }

        if (success) {
          setStatus(`✅ Codificación ${i + 1} funcionó!`);
          return;
        }
      }

      throw new Error('Ninguna codificación funcionó');

    } catch (error) {
      console.error('Error:', error);
      setStatus(`❌ Error: ${error.message}`);
    } finally {
      setIsPrinting(false);
    }
  };

  // Función de diagnóstico completo
  const runDiagnostics = async () => {
    if (!connectedDevice) {
      alert('No hay dispositivo conectado');
      return;
    }

    try {
      const { server, device } = connectedDevice;
      console.log('=== DIAGNÓSTICO COMPLETO ===');
      console.log('Dispositivo:', device.name);
      console.log('ID:', device.id);
      console.log('Conectado:', server.connected);

      const services = await server.getPrimaryServices();
      console.log('Servicios encontrados:', services.length);

      for (const service of services) {
        console.log(`Servicio: ${service.uuid}`);
        const characteristics = await service.getCharacteristics();
        console.log(`  Características: ${characteristics.length}`);
        for (const char of characteristics) {
          console.log(`    ${char.uuid} - Propiedades:`, char.properties);
        }
      }

      alert('Diagnóstico completado. Revisa la consola para detalles.');
    } catch (error) {
      console.error('Error en diagnóstico:', error);
      alert(`Error en diagnóstico: ${error.message}`);
    }
  };

  const disconnectDevice = () => {
    if (connectedDevice) {
      const { device, server } = connectedDevice;
      device.removeEventListener('gattserverdisconnected', onDisconnected);
      if (server.connected) {
        server.disconnect();
      }
    }
    setConnectedDevice(null);
    setScannedDevice(null);
    setStatus('Dispositivo desconectado');
  };

  const showDeviceInfo = async () => {
    if (connectedDevice) {
      try {
        const { device, server } = connectedDevice;
        const services = await server.getPrimaryServices();
        let serviceInfo = '';

        services.forEach(service => {
          serviceInfo += `Servicio: ${service.uuid}\n`;
        });

        alert(`Dispositivo conectado:\nNombre: ${device.name}\nID: ${device.id}\nConectado: ${server.connected}\nServicios:\n${serviceInfo}`);
      } catch (error) {
        alert(`Dispositivo conectado:\nNombre: ${connectedDevice.name}\nError al obtener servicios: ${error.message}`);
      }
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>🖨️ Impresora Bluetooth</h1>
        
        <div className="status-container">
          <div className="status">{status}</div>
        </div>

        <div className="controls">
          {!connectedDevice ? (
            <div className="connection-section">
              <button 
                onClick={scanForDevices}
                disabled={isConnecting}
                className="scan-btn"
              >
                {isConnecting ? '🔄 Escaneando...' : '🔍 Escanear Dispositivos'}
              </button>
              
              {scannedDevice && (
                <button 
                  onClick={connectScannedDevice}
                  disabled={isConnecting}
                  className="connect-scanned-btn"
                >
                  {isConnecting ? '🔄 Conectando...' : `🔌 Conectar ${scannedDevice.name}`}
                </button>
              )}
              
              <button 
                onClick={connectToPrinter}
                disabled={isConnecting}
                className="connect-btn"
              >
                {isConnecting ? '🔄 Conectando...' : '🔍 Buscar Impresora'}
              </button>
              
              <div className="help-text">
                <p>1. Primero escanea para ver dispositivos disponibles</p>
                <p>2. Si encuentras tu impresora, conéctala directamente</p>
                <p>3. O usa "Buscar Impresora" para buscar y conectar en un solo paso</p>
              </div>
            </div>
          ) : (
            <div className="print-section">
              <div className="device-connected">
                <h3>✅ Conectado a: {connectedDevice.name}</h3>
                <button onClick={showDeviceInfo} className="info-btn">
                  ℹ️ Info del Dispositivo
                </button>
              </div>
              
              {/* Nueva sección para imprimir mensaje personalizado */}
              <div className="custom-message-section">
                <h3>📝 Imprimir Mensaje Personalizado</h3>
                <div className="message-input-container">
                  <input
                    type="text"
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Escribe tu mensaje aquí"
                    className="message-input"
                    disabled={isPrinting}
                  />
                  <button
                    onClick={printCustomMessage}
                    disabled={isPrinting || !customMessage.trim()}
                    className="print-message-btn"
                  >
                    {isPrinting ? '🔄 Imprimiendo...' : '🖨️ Imprimir Mensaje'}
                  </button>
                </div>
              </div>
              
              <div className="print-buttons">
                <button 
                  onClick={printInitCommand}
                  disabled={isPrinting}
                  className="init-btn"
                >
                  {isPrinting ? '🔄 Enviando...' : '🔧 Inicializar'}
                </button>
                
                <button 
                  onClick={printSingleLineFeed}
                  disabled={isPrinting}
                  className="feed-btn"
                >
                  {isPrinting ? '🔄 Enviando...' : '📄 Salto de Línea'}
                </button>
                
                <button 
                  onClick={printPlainText}
                  disabled={isPrinting}
                  className="text-btn"
                >
                  {isPrinting ? '🔄 Enviando...' : '📝 Texto Plano'}
                </button>
                
                <button 
                  onClick={printRawBytesAdvanced}
                  disabled={isPrinting}
                  className="raw-btn"
                >
                  {isPrinting ? '🔄 Enviando...' : '📤 Bytes RAW'}
                </button>
                
                <button 
                  onClick={printWithEncodings}
                  disabled={isPrinting}
                  className="encoding-btn"
                >
                  {isPrinting ? '🔄 Enviando...' : '🔤 Codificaciones'}
                </button>
                
                <button 
                  onClick={testAllCharacteristics}
                  disabled={isPrinting}
                  className="test-btn"
                >
                  {isPrinting ? '🔄 Probando...' : '🔍 Probar Todas'}
                </button>

                <button 
                  onClick={runDiagnostics}
                  disabled={isPrinting}
                  className="diagnostic-btn"
                >
                  🔍 Diagnóstico
                </button>
                
                <button 
                  onClick={disconnectDevice}
                  className="disconnect-btn"
                >
                  🔌 Desconectar
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="instructions">
          <h3>🖨️ Imprimir Mensaje Personalizado:</h3>
          <ol>
            <li><strong>Conecta</strong> tu impresora Bluetooth</li>
            <li><strong>Escribe</strong> tu mensaje en el campo de texto</li>
            <li><strong>Haz clic</strong> en "Imprimir Mensaje"</li>
          </ol>
          
          <h3>🔍 Pruebas avanzadas:</h3>
          <ol>
            <li><strong>Inicializar</strong>: Envía comando de inicialización ESC @</li>
            <li><strong>Salto de Línea</strong>: Prueba diferentes tipos de saltos de línea</li>
            <li><strong>Texto Plano</strong>: Envía texto simple sin formato</li>
            <li><strong>Bytes RAW</strong>: Envía comandos binarios básicos</li>
            <li><strong>Codificaciones</strong>: Prueba diferentes formatos de codificación</li>
            <li><strong>Probar Todas</strong>: Intenta escribir en todas las características disponibles</li>
            <li><strong>Diagnóstico</strong>: Muestra información detallada del dispositivo</li>
          </ol>
          
          <h3>💡 Notas:</h3>
          <ul>
            <li>Prueba cada botón en orden</li>
            <li>Revisa la consola (F12) para mensajes detallados</li>
            <li>Si ningún botón funciona, el problema puede ser con la impresora o el Bluetooth</li>
          </ul>
        </div>
      </header>
    </div>
  );
}

export default Print;